/**
 * Resources — public hub listing every long-form asset (white papers,
 * playbooks) Nodera Health publishes.
 *
 * Route: /resources
 *
 * Keeps the sales team a single link to share when the specific asset
 * they need isn't top of mind. New assets get added to
 * `/app/frontend/src/data/whitepapers.js` and show up here automatically.
 */
import { Link } from "react-router-dom";
import { Section, Eyebrow } from "@/components/Section";
import { ArrowRight, Clock, FileText } from "lucide-react";
import { WHITEPAPERS } from "@/data/whitepapers";

export default function Resources() {
  return (
    <div data-testid="resources-hub-page">
      <Section className="pt-28 pb-8">
        <div className="max-w-3xl">
          <Eyebrow>Resources</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            The math behind cash-pay medicine.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Long-form pieces we write when we want to be honest about how this business actually works — the numbers,
            the tradeoffs, and the places we&apos;ve seen clinics get it right (and get it wrong).
          </p>
        </div>
      </Section>

      <Section className="pt-0 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="resources-hub-grid">
          {WHITEPAPERS.map((paper) => (
            <Link
              key={paper.id}
              to={paper.public_path}
              data-testid={`resources-card-${paper.id}`}
              className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:border-blue-300 transition-all"
            >
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600">
                <FileText className="w-3 h-3" /> White paper · {paper.tag}
              </div>
              <h2 className="mt-3 font-display text-xl sm:text-2xl font-bold text-slate-900 leading-tight group-hover:text-blue-700 transition-colors">
                {paper.title}
              </h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
                {paper.summary}
              </p>
              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {paper.read_minutes} min read
                  </span>
                  <span className="text-slate-300">•</span>
                  <span>{paper.published}</span>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">
                  Read <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 max-w-3xl">
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-6 sm:p-8 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">
                Want us to walk you through any of this on your programs?
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Every number in these papers is a screen in Nodera. See it against your clinic in 30 minutes.
              </p>
            </div>
            <Link
              to="/demo"
              data-testid="resources-cta-demo"
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              Book a walkthrough
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
